import { ClerkProvider, useAuth } from "@clerk/clerk-react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { KeyRound } from "lucide-react";
import { type ReactNode, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { FullPageMessage } from "../components/full-page-message";
import { env } from "../config/env";
import { setTokenGetter } from "../lib/api";
import { isClerkPublishableKey } from "../lib/clerk-key";

const ApiAuthBridge = () => {
  const { getToken } = useAuth();
  useEffect(() => setTokenGetter(() => getToken()), [getToken]);
  return null;
};

const SignInNotSetUp = () => (
  <FullPageMessage icon={<KeyRound className="size-5" />} title="Sign-in isn't set up yet">
    <p>
      Add Growth Engine's Clerk publishable key as <code>VITE_CLERK_PUBLISHABLE_KEY</code> in{" "}
      <code>.env</code>, then restart the dev server.
    </p>
  </FullPageMessage>
);

export const Providers = ({ children }: { children: ReactNode }) => {
  const navigate = useNavigate();
  const [queryClient] = useState(() => new QueryClient());

  if (!isClerkPublishableKey(env.clerkPublishableKey)) return <SignInNotSetUp />;

  return (
    <ClerkProvider
      publishableKey={env.clerkPublishableKey}
      routerPush={(to) => navigate(to)}
      routerReplace={(to) => navigate(to, { replace: true })}
      signInUrl="/sign-in"
      signUpUrl="/sign-up"
      signInFallbackRedirectUrl="/"
      signUpFallbackRedirectUrl="/welcome"
    >
      <ApiAuthBridge />
      <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
    </ClerkProvider>
  );
};
